import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import './Settings.css';

function Settings() {
  const { currentUser, logout } = useAuth();
  const [settings, setSettings] = useState({
    notifications: {
      friendRequests: true,
      bookClubUpdates: true,
      readingReminders: true,
      recommendations: true,
      socialActivity: true
    },
    privacy: {
      profileVisibility: 'friends',
      readingActivityVisible: true,
      bookClubsVisible: true,
      friendsListVisible: true
    },
    reading: {
      defaultPrivacy: 'friends',
      autoBackup: true,
      showProgressUpdates: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('notifications');

  useEffect(() => {
    fetchSettings();
  }, [currentUser]);

  const fetchSettings = async () => {
    if (!currentUser) return;
    
    try {
      const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings(prevSettings => ({
          ...prevSettings,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!currentUser) return;
    
    setSaving(true);
    try {
      await updateDoc(doc(db, 'userSettings', currentUser.uid), {
        ...settings,
        updatedAt: new Date()
      });
      // Show success message (in a real app)
    } catch (error) {
      console.error('Error saving settings:', error);
      // Show error message (in a real app)
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = (key) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key]
      }
    }));
  };

  const handlePrivacyChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [key]: value
      }
    }));
  };

  const handleReadingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      reading: {
        ...prev.reading,
        [key]: value
      }
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  const sections = [
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'privacy', label: 'Privacy', icon: '🔒' },
    { key: 'reading', label: 'Reading', icon: '📚' },
    { key: 'account', label: 'Account', icon: '👤' }
  ];

  return (
    <div className="settings-page">
      <div className="container">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Manage your ChapterChat preferences and account settings</p>
        </div>

        <div className="settings-layout">
          <nav className="settings-nav">
            {sections.map(section => (
              <button
                key={section.key}
                className={`settings-nav-item ${activeSection === section.key ? 'active' : ''}`}
                onClick={() => setActiveSection(section.key)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            ))}
          </nav>

          <div className="settings-content">
            {activeSection === 'notifications' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>🔔 Notification Preferences</h2>
                  <p>Choose which notifications you'd like to receive</p>
                </div>
                
                <div className="settings-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Friend Requests</h4>
                      <p>Get notified when someone sends you a friend request</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications.friendRequests}
                        onChange={() => handleNotificationChange('friendRequests')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Book Club Updates</h4>
                      <p>Notifications about your book club activities and meetings</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications.bookClubUpdates}
                        onChange={() => handleNotificationChange('bookClubUpdates')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Reading Reminders</h4>
                      <p>Daily reminders to help you maintain your reading habit</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications.readingReminders}
                        onChange={() => handleNotificationChange('readingReminders')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Book Recommendations</h4>
                      <p>Get notified when friends recommend books to you</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications.recommendations}
                        onChange={() => handleNotificationChange('recommendations')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Social Activity</h4>
                      <p>Notifications about likes, comments, and friend activity</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notifications.socialActivity}
                        onChange={() => handleNotificationChange('socialActivity')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'privacy' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>🔒 Privacy Settings</h2>
                  <p>Control who can see your information and activity</p>
                </div>
                
                <div className="settings-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Profile Visibility</h4>
                      <p>Who can view your profile information</p>
                    </div>
                    <select
                      value={settings.privacy.profileVisibility}
                      onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                      className="setting-select"
                    >
                      <option value="public">Everyone</option>
                      <option value="friends">Friends Only</option>
                      <option value="private">Only Me</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Reading Activity</h4>
                      <p>Show your reading progress and book updates to others</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.privacy.readingActivityVisible}
                        onChange={() => handlePrivacyChange('readingActivityVisible', !settings.privacy.readingActivityVisible)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Book Clubs</h4>
                      <p>Show your book club memberships on your profile</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.privacy.bookClubsVisible}
                        onChange={() => handlePrivacyChange('bookClubsVisible', !settings.privacy.bookClubsVisible)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Friends List</h4>
                      <p>Allow others to see your friends list</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.privacy.friendsListVisible}
                        onChange={() => handlePrivacyChange('friendsListVisible', !settings.privacy.friendsListVisible)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'reading' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>📚 Reading Preferences</h2>
                  <p>Customize your reading tracking experience</p>
                </div>
                
                <div className="settings-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Default Privacy for New Books</h4>
                      <p>Default visibility setting when you add new books</p>
                    </div>
                    <select
                      value={settings.reading.defaultPrivacy}
                      onChange={(e) => handleReadingChange('defaultPrivacy', e.target.value)}
                      className="setting-select"
                    >
                      <option value="public">Public</option>
                      <option value="friends">Friends Only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Auto Backup</h4>
                      <p>Automatically backup your reading data to the cloud</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.reading.autoBackup}
                        onChange={() => handleReadingChange('autoBackup', !settings.reading.autoBackup)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Show Progress Updates</h4>
                      <p>Share reading progress updates with your friends</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.reading.showProgressUpdates}
                        onChange={() => handleReadingChange('showProgressUpdates', !settings.reading.showProgressUpdates)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'account' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>👤 Account Management</h2>
                  <p>Manage your account and data</p>
                </div>
                
                <div className="settings-group">
                  <div className="account-info">
                    <h4>Account Information</h4>
                    <p><strong>Email:</strong> {currentUser?.email}</p>
                    <p><strong>Account created:</strong> {currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'Unknown'}</p>
                  </div>

                  <div className="account-actions">
                    <h4>Account Actions</h4>
                    <div className="action-buttons">
                      <button className="btn btn-secondary">
                        📥 Export Data
                      </button>
                      <button className="btn btn-secondary">
                        🔄 Reset Settings
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={handleLogout}
                      >
                        🚪 Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="settings-footer">
              <button 
                className="btn btn-primary"
                onClick={saveSettings}
                disabled={saving}
              >
                {saving ? '💾 Saving...' : '💾 Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;